import { useEffect, useState } from 'react';
import { createEvent, getEvent, updateEvent } from '../api.js';
import { showBackButton } from '../lib/twa.js';

function toLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const tz = d.getTimezoneOffset() * 60_000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

function toUtcIso(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function validate(values) {
  const errors = {};
  if (!values.title || values.title.trim().length === 0) {
    errors.title = 'Title is required';
  } else if (values.title.length > 200) {
    errors.title = 'Max 200 characters';
  }
  if (values.description && values.description.length > 4000) {
    errors.description = 'Max 4000 characters';
  }
  if (values.location && values.location.length > 200) {
    errors.location = 'Max 200 characters';
  }
  if (!values.startsAt) {
    errors.startsAt = 'Start time is required';
  }
  if (values.endsAt && values.endsAt && values.startsAt && values.endsAt < values.startsAt) {
    errors.endsAt = 'End must be after start';
  }
  const rem = Number.parseInt(values.reminder, 10);
  if (!Number.isInteger(rem) || rem < 0 || rem > 10080) {
    errors.reminder = 'Reminder must be 0–10080 minutes';
  }
  return errors;
}

export default function EventForm({ chat, eventId }) {
  const [values, setValues] = useState({
    title: '',
    description: '',
    startsAt: '',
    endsAt: '',
    location: '',
    reminder: '60',
    notifyNewGoing: false,
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState(null);
  const isEdit = Boolean(eventId);

  useEffect(() => {
    const hide = showBackButton(() => {
      window.location.hash = '#/';
    });
    return () => hide();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const result = await getEvent(eventId);
        const ev = result.event;
        setValues({
          title: ev.title || '',
          description: ev.description || '',
          startsAt: toLocalInput(ev.starts_at),
          endsAt: toLocalInput(ev.ends_at),
          location: ev.location || '',
          reminder: String(ev.reminder_lead_minutes ?? 60),
          notifyNewGoing: Boolean(ev.notify_new_going),
        });
      } catch (e) {
        setServerError(e);
      }
    })();
  }, [eventId, isEdit]);

  function update(field, value) {
    setValues((v) => ({ ...v, [field]: value }));
  }

  async function submit() {
    setServerError(null);
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const payload = {
      title: values.title.trim(),
      description: values.description || null,
      starts_at: toUtcIso(values.startsAt),
      ends_at: values.endsAt ? toUtcIso(values.endsAt) : null,
      location: values.location || null,
      reminder_lead_minutes: Number.parseInt(values.reminder, 10),
      notify_new_going: values.notifyNewGoing ? 1 : 0,
    };
    setBusy(true);
    try {
      if (isEdit) {
        await updateEvent(eventId, payload);
      } else {
        await createEvent(chat.id, payload);
      }
      window.location.hash = '#/';
    } catch (e) {
      setServerError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 className="page-header">{isEdit ? 'Edit event' : 'New event'}</h1>
      <div className="col">
        <div className="field">
          <label>Title</label>
          <input value={values.title} onChange={(e) => update('title', e.target.value)} maxLength={200} />
          {errors.title && <div className="error">{errors.title}</div>}
        </div>
        <div className="field">
          <label>Description</label>
          <textarea value={values.description} onChange={(e) => update('description', e.target.value)} maxLength={4000} />
          {errors.description && <div className="error">{errors.description}</div>}
        </div>
        <div className="field">
          <label>Starts at</label>
          <input type="datetime-local" value={values.startsAt} onChange={(e) => update('startsAt', e.target.value)} />
          {errors.startsAt && <div className="error">{errors.startsAt}</div>}
        </div>
        <div className="field">
          <label>Ends at (optional)</label>
          <input type="datetime-local" value={values.endsAt} onChange={(e) => update('endsAt', e.target.value)} />
          {errors.endsAt && <div className="error">{errors.endsAt}</div>}
        </div>
        <div className="field">
          <label>Location</label>
          <input value={values.location} onChange={(e) => update('location', e.target.value)} maxLength={200} />
          {errors.location && <div className="error">{errors.location}</div>}
        </div>
        <div className="field">
          <label>Reminder before (minutes)</label>
          <input type="number" min={0} max={10080} value={values.reminder} onChange={(e) => update('reminder', e.target.value)} />
          {errors.reminder && <div className="error">{errors.reminder}</div>}
        </div>
        <div className="field field-check">
          <input
            id="notify-new-going"
            type="checkbox"
            checked={values.notifyNewGoing}
            onChange={(e) => update('notifyNewGoing', e.target.checked)}
          />
          <label htmlFor="notify-new-going">Уведомлять меня, когда кто-то присоединится</label>
        </div>
        {serverError && <div className="error">{serverError.message}</div>}
        {busy && <div className="meta">Saving…</div>}
        <button onClick={submit} disabled={busy}>{isEdit ? 'Save' : 'Create'}</button>
      </div>
    </div>
  );
}
